/* ==========================================================================
   Freshfield — Magnesium label-math calculator (Shopify section script)
   File: assets/lp-mg-calculator.js

   Ported from the CHFA label-math calculator (Freshfield-Magnesium-Calculator,
   template.html, calculate()) with the honesty rules kept intact:

     * The illustration only. Capsule size and powder density are illustrative
       ranges, never a measurement or a claim about another bottle.
     * It is not a composition test and not an absorption test. No absorbed
       dose is estimated, and no oxide (or competitor) verdict is produced.
     * No product-specific verdict: the result never evaluates a named product.
       The only Freshfield statement is a separately labelled product fact.

   What this file deliberately does NOT do
     * no service worker, no network request, no storage, no third-party code
     * no <input>, no <form> (the landing page is a read-only surface; the
       amount is a WAI-ARIA spinbutton and each single-choice group is a
       role=radiogroup of role=radio buttons, roving tabindex, arrow keys)
     * no inline markup: every visible word, including the runtime verdict and
       the math rows, comes from the locale blob in the section

   Host contract (one or more instances per page)
     [data-ffm-calc]
       [data-ffm-calc-strings] ....... type=application/json locale strings
       [data-ffm-calc-amount] ........ role=spinbutton, tabindex=0
         [data-ffm-calc-amount-value]  amount digits
         [data-ffm-calc-amount-unit]   localized unit token
       [data-ffm-calc-step="1|-1"] ... nudge buttons
       [data-ffm-calc-group="basis|serving|size|form"]
         [role=radio][data-value] ...  roving tabindex, aria-checked
       [data-ffm-calc-serving] ....... the serving-count field (hidden for
                                       a per-capsule amount)
       [data-ffm-calc-reset] ......... back to the 200 mg example
       [data-ffm-calc-result] ........ data-status drives the state styling
       [data-ffm-calc-assumption] .... the assumption line for the estimate
       [data-ffm-calc-label="claim|maximum"]
       [data-ffm-calc-value="verdict|claim|maximum|gap|explanation|
                             row-elemental|row-percent|row-powder|row-fill|
                             row-ceiling"]

   The pure arithmetic is exported (module.exports in Node, window.FFMCalcEngine
   in a browser) so harness/calculator_unit.cjs can test it without a DOM.
   ========================================================================== */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------- */
  /* Engines constants (theoretical anhydrous composition, illustrative)     */
  /* ---------------------------------------------------------------------- */
  var PERCENT = { bisglycinate: 0.141, citrate: 0.16, oxide: 0.6 };
  var VOLUME = { '0': 0.68, '00': 0.9, '000': 1.37 };
  var FILL_LOW = 0.6;   /* g/mL, illustrative low end  */
  var FILL_HIGH = 1.0;  /* g/mL, illustrative high end */
  var MIN_AMOUNT = 0.1;
  var MAX_AMOUNT = 2000;
  var STEP = 1;
  var STEP_LARGE = 10;
  var BASIS_IDS = ['capsule', 'serving'];
  var SIZE_IDS = ['0', '00', '000'];
  var FORM_IDS = ['bisglycinate', 'citrate', 'oxide'];
  var SERVING_IDS = ['2', '3', '4'];
  var AMOUNT_RE = /^\d+(?:[.,]\d{1,2})?$/;

  var DEFAULTS = { amount: '200', basis: 'capsule', servings: '2', size: '00', form: 'bisglycinate' };

  /* Locale blob keys that carry the runtime copy, so the DOM layer never
     hard-codes a sentence. */
  var COPY_KEYS = {
    verdictInvalid: 'verdictInvalid',
    verdictPossible: 'verdictPossible',
    verdictNear: 'verdictNear',
    verdictUnproven: 'verdictUnproven',
    verdictOutside: 'verdictOutsidePattern',
    gapPossible: 'gapPossible',
    gapNear: 'gapNear',
    gapUnproven: 'gapUnproven',
    gapOutside: 'gapOutsidePattern',
    explainInvalid: 'explainInvalid',
    explainPossible: 'explainPossiblePattern',
    explainNear: 'explainNearPattern',
    explainUnproven: 'explainUnprovenPattern',
    explainOutside: 'explainOutsidePattern',
    claimCapsule: 'claimLabelCapsule',
    claimServing: 'claimLabelServing',
    maxBisglycinate: 'maxLabelBisglycinate',
    maxCitrate: 'maxLabelCitrate',
    maxOxide: 'maxLabelOxide'
  };

  var MAX_LABEL_KEY = { bisglycinate: 'maxBisglycinate', citrate: 'maxCitrate', oxide: 'maxOxide' };
  var FORM_LABEL_KEY = { bisglycinate: 'formBisglycinate', citrate: 'formCitrate', oxide: 'formOxide' };
  var FORM_INLINE_KEY = { bisglycinate: 'formBisglycinateInline', citrate: 'formCitrateInline', oxide: 'formOxideInline' };

  var UNIT = {
    mg: '{{ value }} mg',
    percent: '{{ value }}%',
    range: '{{ low }}–{{ high }} mg'
  };

  /* ---------------------------------------------------------------------- */
  /* Pure helpers                                                            */
  /* ---------------------------------------------------------------------- */
  function parseAmount(raw) {
    var text = String(raw === undefined || raw === null ? '' : raw).trim();
    if (!AMOUNT_RE.test(text)) return NaN;
    var value = Number(text.replace(',', '.'));
    if (!isFinite(value) || value < MIN_AMOUNT || value > MAX_AMOUNT) return NaN;
    return value;
  }

  function fill(pattern, values) {
    return String(pattern === undefined || pattern === null ? '' : pattern)
      .replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, function (match, name) {
        return Object.prototype.hasOwnProperty.call(values, name) ? String(values[name]) : match;
      });
  }

  /* The illustration has no precision beyond a milligram, so derived ranges are
     rounded to six decimals: this keeps the arithmetic identical to a hand
     calculation instead of accumulating IEEE-754 noise (0.68 mL * 600 must be
     408, not 408.00000000000006). */
  function round6(value) {
    return Math.round(value * 1e6) / 1e6;
  }

  function localeTag(code) {
    var text = String(code || '').trim();
    if (text === 'en') return 'en-CA';
    if (text === 'fr') return 'fr-CA';
    return text || 'en-CA';
  }

  function makeFormatter(locale, patterns) {
    var tag = localeTag(locale);
    var rules = patterns || UNIT;
    /* The locale files use ordinary spaces; ICU emits U+00A0 (and U+202F in
       some builds) for a French thousands separator. Normalize so the runtime
       string is byte-identical to the reviewed translation. */
    function tidy(text) {
      return String(text).replace(/[\u00a0\u202f]/g, ' ');
    }
    function count(value, digits) {
      try {
        return tidy(new Intl.NumberFormat(tag, {
          maximumFractionDigits: digits === undefined ? 0 : digits,
          minimumFractionDigits: 0
        }).format(value));
      } catch (err) {
        return String(value);
      }
    }
    return {
      count: count,
      mg: function (value, digits) { return fill(rules.mg, { value: count(value, digits) }); },
      percent: function (value, digits) { return fill(rules.percent, { value: count(value, digits) }); },
      range: function (low, high, digits) {
        return fill(rules.range, { low: count(low, digits), high: count(high, digits) });
      }
    };
  }

  function pick(list, value, fallback) {
    return list.indexOf(String(value)) > -1 ? String(value) : fallback;
  }

  function clampAmount(value) {
    if (!isFinite(value)) return MIN_AMOUNT;
    if (value > MAX_AMOUNT) return MAX_AMOUNT;
    if (value < MIN_AMOUNT) return MIN_AMOUNT;
    return Math.round(value * 100) / 100;
  }

  function editableAmount(value, locale) {
    var text = String(Math.round(value * 100) / 100);
    return locale === 'fr' ? text.replace('.', ',') : text;
  }

  /* ---------------------------------------------------------------------- */
  /* The arithmetic                                                          */
  /* ---------------------------------------------------------------------- */
  function compute(input, fmt, strings) {
    var data = input || {};
    var copy = strings || {};
    var format = fmt || makeFormatter('en', UNIT);

    var basis = pick(BASIS_IDS, data.basis, 'capsule');
    var size = pick(SIZE_IDS, data.size, '00');
    var form = pick(FORM_IDS, data.form, 'bisglycinate');
    var servings = pick(SERVING_IDS, data.servings, '2');

    var volume = VOLUME[size];
    var percent = PERCENT[form];
    /* mL x g/mL -> g; x 1000 -> mg */
    var low = round6(volume * FILL_LOW * 1000);
    var high = round6(volume * FILL_HIGH * 1000);
    var amount = parseAmount(data.amount);

    var result = {
      ok: false,
      status: 'invalid',
      basis: basis,
      size: size,
      form: form,
      servings: servings,
      volume: volume,
      percent: percent,
      low: low,
      high: high,
      amount: amount,
      perCap: NaN,
      powder: NaN,
      maxElemental: NaN,
      gap: NaN,
      values: {},
      copy: {
        assumption: 'assumptionPattern',
        claimLabel: basis === 'serving' ? COPY_KEYS.claimServing : COPY_KEYS.claimCapsule,
        maxLabel: COPY_KEYS[MAX_LABEL_KEY[form]],
        verdict: COPY_KEYS.verdictInvalid,
        gap: null,
        explanation: COPY_KEYS.explainInvalid
      }
    };

    var base = {
      size: size,
      form: copy[FORM_LABEL_KEY[form]] === undefined ? form : copy[FORM_LABEL_KEY[form]],
      formInline: copy[FORM_INLINE_KEY[form]] === undefined ? form : copy[FORM_INLINE_KEY[form]],
      volume: format.count(volume, 2)
    };

    if (!isFinite(amount)) {
      result.values = {
        claim: '',
        maximum: '',
        powder: '',
        capacity: '',
        range: '',
        percent: '',
        gap: '',
        size: base.size,
        volume: base.volume,
        form: base.form,
        formInline: base.formInline
      };
      return result;
    }

    var perCap = amount / (basis === 'serving' ? Number(servings) : 1);
    perCap = round6(perCap);
    var powder = round6(perCap / percent);
    var maxElemental = round6(high * percent);

    var status;
    var verdictKey;
    var gapKey;
    var explainKey;

    if (powder > high * 1.05) {
      status = 'outside';
      verdictKey = COPY_KEYS.verdictOutside;
      gapKey = COPY_KEYS.gapOutside;
      explainKey = COPY_KEYS.explainOutside;
    } else if (powder > high) {
      status = 'near';
      verdictKey = COPY_KEYS.verdictNear;
      gapKey = COPY_KEYS.gapNear;
      explainKey = COPY_KEYS.explainNear;
    } else if (powder > low) {
      status = 'unproven';
      verdictKey = COPY_KEYS.verdictUnproven;
      gapKey = COPY_KEYS.gapUnproven;
      explainKey = COPY_KEYS.explainUnproven;
    } else {
      status = 'possible';
      verdictKey = COPY_KEYS.verdictPossible;
      gapKey = COPY_KEYS.gapPossible;
      explainKey = COPY_KEYS.explainPossible;
    }

    result.ok = true;
    result.status = status;
    result.perCap = perCap;
    result.powder = powder;
    result.maxElemental = maxElemental;
    result.gap = round6(perCap - maxElemental);
    result.copy.verdict = verdictKey;
    result.copy.gap = gapKey;
    result.copy.explanation = explainKey;
    result.values = {
      claim: format.mg(perCap, 2),
      maximum: format.mg(maxElemental, 0),
      powder: format.mg(powder, 0),
      capacity: format.mg(high, 0),
      range: format.range(low, high, 0),
      percent: format.percent(percent * 100, 1),
      gap: format.mg(Math.floor(perCap - maxElemental), 0),
      size: base.size,
      volume: base.volume,
      form: base.form,
      formInline: base.formInline
    };
    return result;
  }

  var engine = {
    PERCENT: PERCENT,
    VOLUME: VOLUME,
    FILL_LOW: FILL_LOW,
    FILL_HIGH: FILL_HIGH,
    MIN_AMOUNT: MIN_AMOUNT,
    MAX_AMOUNT: MAX_AMOUNT,
    STEP: STEP,
    STEP_LARGE: STEP_LARGE,
    DEFAULTS: DEFAULTS,
    UNIT: UNIT,
    COPY_KEYS: COPY_KEYS,
    MAX_LABEL_KEY: MAX_LABEL_KEY,
    FORM_LABEL_KEY: FORM_LABEL_KEY,
    FORM_INLINE_KEY: FORM_INLINE_KEY,
    parseAmount: parseAmount,
    fill: fill,
    clampAmount: clampAmount,
    localeTag: localeTag,
    makeFormatter: makeFormatter,
    compute: compute
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = engine;
  if (typeof window !== 'undefined') window.FFMCalcEngine = engine;
  if (typeof document === 'undefined') return;

  /* ---------------------------------------------------------------------- */
  /* DOM layer                                                               */
  /* ---------------------------------------------------------------------- */
  function readStrings(root) {
    var node = root.querySelector('[data-ffm-calc-strings]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}') || {};
    } catch (err) {
      return {};
    }
  }

  function pickText(strings, key) {
    if (!key) return '';
    var value = strings[key];
    return value === undefined || value === null ? '' : String(value);
  }

  function Controller(root) {
    this.root = root;
    this.strings = readStrings(root);
    this.patterns = {
      mg: pickText(this.strings, 'mgPattern') || UNIT.mg,
      percent: pickText(this.strings, 'percentPattern') || UNIT.percent,
      range: pickText(this.strings, 'rangePattern') || UNIT.range
    };
    this.fmt = makeFormatter(pickText(this.strings, 'locale') || 'en', this.patterns);
    this.result = root.querySelector('[data-ffm-calc-result]');
    this.assumption = root.querySelector('[data-ffm-calc-assumption]');
    this.servingField = root.querySelector('[data-ffm-calc-serving]');
    this.amountNode = root.querySelector('[data-ffm-calc-amount]');
    this.amountValue = root.querySelector('[data-ffm-calc-amount-value]');
    this.amountUnit = root.querySelector('[data-ffm-calc-amount-unit]');
    this.groups = {};
    var self = this;
    Array.prototype.forEach.call(root.querySelectorAll('[data-ffm-calc-group]'), function (group) {
      self.groups[group.getAttribute('data-ffm-calc-group')] = group;
    });
    this.state = {
      amount: DEFAULTS.amount,
      basis: DEFAULTS.basis,
      servings: DEFAULTS.servings,
      size: DEFAULTS.size,
      form: DEFAULTS.form
    };
    this.lastValid = Number(DEFAULTS.amount);
    this.servingsToggle();
    this.syncGroups();
  }

  Controller.prototype.groupValue = function (name) {
    var group = this.groups[name];
    if (!group) return '';
    var checked = group.querySelector('[role="radio"][aria-checked="true"]');
    return checked ? checked.getAttribute('data-value') : '';
  };

  Controller.prototype.radios = function (name) {
    var group = this.groups[name];
    return group ? Array.prototype.slice.call(group.querySelectorAll('[role="radio"]')) : [];
  };

  Controller.prototype.syncGroups = function () {
    var self = this;
    Object.keys(this.groups).forEach(function (name) {
      var current = self.state[name];
      var radios = self.radios(name);
      var matched = false;
      radios.forEach(function (radio) {
        var on = radio.getAttribute('data-value') === current;
        radio.setAttribute('aria-checked', on ? 'true' : 'false');
        if (on) {
          radio.setAttribute('tabindex', '0');
          matched = true;
        } else {
          radio.setAttribute('tabindex', '-1');
        }
      });
      if (!matched && radios.length) {
        radios[0].setAttribute('aria-checked', 'true');
        radios[0].setAttribute('tabindex', '0');
        self.state[name] = radios[0].getAttribute('data-value');
      }
    });
    this.servingsToggle();
  };

  Controller.prototype.servingsToggle = function () {
    if (!this.servingField) return;
    this.servingField.hidden = this.state.basis !== 'serving';
  };

  Controller.prototype.set = function (name, value) {
    if (!(name in this.state)) return;
    this.state[name] = value;
    this.syncGroups();
    this.render();
  };

  Controller.prototype.setAmount = function (raw, normalize) {
    this.state.amount = String(raw);
    if (normalize) {
      var value = parseAmount(this.state.amount);
      if (isFinite(value)) this.state.amount = editableAmount(value, this.strings.locale);
    }
    this.render();
  };

  Controller.prototype.nudge = function (delta) {
    var value = parseAmount(this.state.amount);
    if (!isFinite(value)) value = this.lastValid;
    this.setAmount(editableAmount(clampAmount(value + delta), this.strings.locale));
  };

  Controller.prototype.jump = function (to) {
    this.setAmount(editableAmount(to, this.strings.locale));
  };

  Controller.prototype.type = function (chunk) {
    this.state.amount = (this.state.amount + chunk).replace(/^0+(?=\d)/, '');
    this.render();
  };

  Controller.prototype.backspace = function () {
    this.state.amount = this.state.amount.slice(0, -1);
    this.render();
  };

  Controller.prototype.write = function (selector, text) {
    var node = this.root.querySelector(selector);
    if (node) node.textContent = text;
  };

  Controller.prototype.render = function () {
    var state = compute(this.state, this.fmt, this.strings);
    var values = state.values;

    if (this.result) this.result.setAttribute('data-status', state.status);

    if (this.assumption) {
      var pattern = pickText(this.strings, state.copy.assumption);
      this.assumption.textContent = fill(pattern, {
        size: state.values.size || state.size,
        volume: state.values.volume || this.fmt.count(state.volume, 2)
      });
    }

    var claimLabel = this.root.querySelector('[data-ffm-calc-label="claim"]');
    if (claimLabel) claimLabel.textContent = pickText(this.strings, state.copy.claimLabel);
    var maxLabel = this.root.querySelector('[data-ffm-calc-label="maximum"]');
    if (maxLabel) maxLabel.textContent = pickText(this.strings, state.copy.maxLabel);

    var verdict = pickText(this.strings, state.copy.verdict);
    if (state.copy.verdict === COPY_KEYS.verdictOutside) {
      verdict = fill(verdict, { form: values.form });
    }
    this.write('[data-ffm-calc-value="verdict"]', verdict);

    this.write('[data-ffm-calc-value="claim"]', values.claim);
    this.write('[data-ffm-calc-value="maximum"]', values.maximum);

    var gapNode = this.root.querySelector('[data-ffm-calc-value="gap"]');
    if (gapNode) {
      gapNode.textContent = state.copy.gap ? fill(pickText(this.strings, state.copy.gap), values) : '';
    }

    /* Sentences name the form mid-phrase, so they take the lower-case form
       name; the verdict headline takes the capitalised choice label. */
    var fillValues = {};
    var key;
    for (key in values) {
      if (Object.prototype.hasOwnProperty.call(values, key)) fillValues[key] = values[key];
    }
    fillValues.form = values.formInline || values.form;
    var explain = pickText(this.strings, state.copy.explanation);
    if (state.status !== 'invalid') explain = fill(explain, fillValues);
    this.write('[data-ffm-calc-value="explanation"]', explain);

    this.write('[data-ffm-calc-value="row-elemental"]', values.claim);
    this.write('[data-ffm-calc-value="row-percent"]', values.percent);
    this.write('[data-ffm-calc-value="row-powder"]', values.powder);
    this.write('[data-ffm-calc-value="row-fill"]', values.range);
    this.write('[data-ffm-calc-value="row-ceiling"]', values.maximum);

    if (this.amountNode) {
      if (this.amountNode.value !== this.state.amount) this.amountNode.value = this.state.amount;
      if (state.ok) {
        this.lastValid = state.amount;
        this.amountNode.removeAttribute('aria-invalid');
      } else {
        this.amountNode.setAttribute('aria-invalid', 'true');
      }
    }
    this.state.copy = state.copy;
    return state;
  };

  Controller.prototype.reset = function () {
    this.state = {
      amount: DEFAULTS.amount,
      basis: DEFAULTS.basis,
      servings: DEFAULTS.servings,
      size: DEFAULTS.size,
      form: DEFAULTS.form
    };
    this.lastValid = Number(DEFAULTS.amount);
    this.syncGroups();
    this.render();
    if (this.amountNode) {
      try { this.amountNode.focus({ preventScroll: true }); } catch (err) { this.amountNode.focus(); }
    }
  };

  Controller.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var node = event.target;
      if (!node || !node.closest) return;
      var step = node.closest('[data-ffm-calc-step]');
      if (step && self.root.contains(step)) {
        event.preventDefault();
        self.nudge(Number(step.getAttribute('data-ffm-calc-step')) * STEP);
        return;
      }
      var radio = node.closest('[role="radio"][data-value]');
      if (radio && self.root.contains(radio)) {
        event.preventDefault();
        var group = radio.closest('[data-ffm-calc-group]');
        var name = group && group.getAttribute ? group.getAttribute('data-ffm-calc-group') : null;
        if (name) self.set(name, radio.getAttribute('data-value'));
        return;
      }
      var reset = node.closest('[data-ffm-calc-reset]');
      if (reset && self.root.contains(reset)) {
        event.preventDefault();
        self.reset();
      }
    });

    this.root.addEventListener('keydown', function (event) {
      var node = event.target;
      if (!node || !node.closest) return;

      if (self.amountNode && (node === self.amountNode || self.amountNode.contains(node))) {
        if (event.key === 'ArrowUp') { self.nudge(STEP); event.preventDefault(); }
        else if (event.key === 'ArrowDown') { self.nudge(-STEP); event.preventDefault(); }
        else if (event.key === 'PageUp') { self.nudge(STEP_LARGE); event.preventDefault(); }
        else if (event.key === 'PageDown') { self.nudge(-STEP_LARGE); event.preventDefault(); }
        return;
      }
      var radio = node.closest('[role="radio"][data-value]');
      if (radio && self.root.contains(radio)) {
        var parent = radio.closest('[data-ffm-calc-group]');
        var name = parent && parent.getAttribute ? parent.getAttribute('data-ffm-calc-group') : null;
        if (name && self.radioKey(event, radio, name)) event.preventDefault();
      }
    });

    if (this.amountNode) {
      this.amountNode.addEventListener('input', function () {
        self.state.amount = self.amountNode.value;
        self.render();
      });
      this.amountNode.addEventListener('blur', function () {
        var value = parseAmount(self.state.amount);
        if (isFinite(value)) self.setAmount(editableAmount(value, self.strings.locale));
        else self.render();
      });
    }
  };

  Controller.prototype.radioKey = function (event, radio, name) {
    var radios = this.radios(name);
    var index = radios.indexOf(radio);
    if (index < 0) return false;
    var next = -1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % radios.length;
    else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + radios.length) % radios.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = radios.length - 1;
    else return false;
    this.set(name, radios[next].getAttribute('data-value'));
    try { radios[next].focus({ preventScroll: true }); } catch (err) { radios[next].focus(); }
    return true;
  };

  Controller.prototype.spinKey = function (event) {
    var key = event.key;
    if (key === 'ArrowUp' || key === 'ArrowRight') { this.nudge(STEP); return true; }
    if (key === 'ArrowDown' || key === 'ArrowLeft') { this.nudge(-STEP); return true; }
    if (key === 'PageUp') { this.nudge(STEP_LARGE); return true; }
    if (key === 'PageDown') { this.nudge(-STEP_LARGE); return true; }
    if (key === 'Home') { this.jump(MIN_AMOUNT); return true; }
    if (key === 'End') { this.jump(MAX_AMOUNT); return true; }
    if (key === 'Backspace') { this.backspace(); return true; }
    if (key === 'Delete' || key === 'Clear') { this.setAmount(''); return true; }
    if (key === 'Enter') {
      var value = parseAmount(this.state.amount);
      if (isFinite(value)) this.setAmount(this.fmt.count(value, 2));
      else this.render();
      return true;
    }
    if (key === 'Escape' || key === 'Esc') { this.setAmount(this.fmt.count(this.lastValid, 2)); return true; }
    if (key === ' ' || key === 'Spacebar') return true;
    if (key && key.length === 1 && /[0-9]/.test(key)) { this.type(key); return true; }
    if (key === '.' || key === ',') {
      if (this.state.amount.indexOf('.') === -1 && this.state.amount.indexOf(',') === -1) this.type(key);
      return true;
    }
    return false;
  };

  function boot() {
    var roots = document.querySelectorAll('[data-ffm-calc]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.getAttribute('data-ffm-calc-ready') === '1') return;
      root.setAttribute('data-ffm-calc-ready', '1');
      var controller = new Controller(root);
      controller.bind();
      controller.render();
    });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports.boot = boot;
    module.exports.Controller = Controller;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
